import React, { useState, useEffect } from 'react';
import { fetchChoices, createChoice, updateChoice, deleteChoice } from './api';
import './ChoiceManager.css';

const ChoiceManager = () => {
  const [choices, setChoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('contact');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingChoice, setEditingChoice] = useState(null);
  const [formData, setFormData] = useState({
    choice_type: 'contact',
    value: '',
    display_name: '',
    order: 0,
    is_active: true
  });

  const choiceTypes = [
    { key: 'contact', label: 'Contact' },
    { key: 'service', label: 'Service' },
    { key: 'status', label: 'Status' }
  ];

  useEffect(() => {
    loadChoices();
  }, []);

  const loadChoices = async () => {
    try {
      setLoading(true);
      const data = await fetchChoices();
      setChoices(data);
      setError('');
    } catch (error) {
      console.error('Error loading choices:', error);
      setError('Failed to load choices');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormData(prev => ({ ...prev, choice_type: tab }));
  };

  const handleCreateNew = () => {
    setFormData({
      choice_type: activeTab,
      value: '',
      display_name: '',
      order: (choices[activeTab]?.length || 0) + 1,
      is_active: true
    });
    setEditingChoice(null);
    setShowCreateForm(true);
  };

  const handleEdit = (choice) => {
    setFormData({
      choice_type: choice.choice_type,
      value: choice.value,
      display_name: choice.display_name,
      order: choice.order,
      is_active: choice.is_active
    });
    setEditingChoice(choice);
    setShowCreateForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingChoice) {
        await updateChoice(editingChoice.id, formData);
      } else {
        await createChoice(formData);
      }
      
      setShowCreateForm(false);
      setEditingChoice(null);
      await loadChoices();
    } catch (error) {
      console.error('Error saving choice:', error);
      if (error.response?.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.values(errors).flat();
          setError(errorMessages.join(', '));
        } else {
          setError('Failed to save choice');
        }
      } else {
        setError('Failed to save choice');
      }
    }
  };

  const handleDelete = async (choice) => {
    if (window.confirm(`Are you sure you want to delete "${choice.display_name}"?`)) {
      try {
        await deleteChoice(choice.id);
        await loadChoices();
      } catch (error) {
        console.error('Error deleting choice:', error);
        setError('Failed to delete choice');
      }
    }
  };

  const handleToggleActive = async (choice) => {
    try {
      await updateChoice(choice.id, { is_active: !choice.is_active });
      await loadChoices();
    } catch (error) {
      console.error('Error toggling choice:', error);
      setError('Failed to update choice');
    }
  };

  if (loading) {
    return <div className="choice-manager-loading">Loading choices...</div>;
  }

  return (
    <div className="choice-manager">
      <div className="choice-manager-header">
        <h3>Manage Choices</h3>
        <button 
          className="create-choice-btn"
          onClick={handleCreateNew}
        >
          Add New {choiceTypes.find(t => t.key === activeTab)?.label}
        </button>
      </div>

      <div className="choice-tabs">
        {choiceTypes.map(type => (
          <button
            key={type.key}
            className={`tab-button ${activeTab === type.key ? 'active' : ''}`}
            onClick={() => handleTabChange(type.key)}
          >
            {type.label}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="choices-list">
        {choices[activeTab] && choices[activeTab].length > 0 ? (
          <div className="choices-grid">
            {choices[activeTab]
              .sort((a, b) => a.order - b.order)
              .map(choice => (
                <div key={choice.id} className={`choice-card ${!choice.is_active ? 'inactive' : ''}`}>
                  <div className="choice-header">
                    <h4>{choice.display_name}</h4>
                    <div className="choice-actions">
                      <button
                        className={`toggle-btn ${choice.is_active ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(choice)}
                        title={choice.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {choice.is_active ? '✓' : '✗'}
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(choice)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(choice)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="choice-details">
                    <p><strong>Value:</strong> {choice.value}</p>
                    <p><strong>Order:</strong> {choice.order}</p>
                    <p><strong>Status:</strong> {choice.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="no-choices">
            <p>No {choiceTypes.find(t => t.key === activeTab)?.label.toLowerCase()} choices found.</p>
            <button 
              className="create-choice-btn"
              onClick={handleCreateNew}
            >
              Create First {choiceTypes.find(t => t.key === activeTab)?.label}
            </button>
          </div>
        )}
      </div>

      {showCreateForm && (
        <div className="choice-form-overlay">
          <div className="choice-form">
            <h3>{editingChoice ? 'Edit Choice' : 'Create New Choice'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="value">Value *</label>
                <input
                  type="text"
                  id="value"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  required
                  placeholder="e.g., dave, restoration, etc."
                />
              </div>

              <div className="form-group">
                <label htmlFor="display_name">Display Name *</label>
                <input
                  type="text"
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  required
                  placeholder="e.g., Dave, Restoration, etc."
                />
              </div>

              <div className="form-group">
                <label htmlFor="order">Order</label>
                <input
                  type="number"
                  id="order"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  min="0"
                />
              </div>

              <div className="form-group checkbox-group">
                <label htmlFor="is_active" className="checkbox-label">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span className="checkbox-text">Active</span>
                </label>
              </div>

              <div className="form-buttons">
                <button type="submit" className="save-btn">
                  {editingChoice ? 'Update' : 'Create'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingChoice(null);
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChoiceManager;
